import { RootState } from '../rootReducer'
import { BASE_URL } from 'gatsby-env-variables'
import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { ControlDError } from './http'
import { SerializedError } from '@reduxjs/toolkit'
import * as errorCodes from './errorCodes'

export type ResponseType<T> = { data: T } & { error: ControlDError }

export const getQueryArgs = <T>(
  url: string,
  method?: string,
  body?: T,
  orgId?: string,
): { url: string; method?: string; body?: T; headers?: Record<string, string> } => {
  const urlParams = new URLSearchParams(window.location.search)
  const subOrganization = urlParams.get('orgId') || orgId || ''

  return {
    url,
    method,
    body,
    // injecting the impersonation header here. This can be removed from the call if needed in the query
    ...(subOrganization ? { headers: { 'x-force-org-id': subOrganization } } : {}),
  }
}

const isPublicEndpoint = (endpoint: string): boolean => {
  return [
    'getProxiesNoAuth',
    'getPublicDomainTest',
    'getClientIPData',
    'getTestResults',
    'startTest',
    'querySubdomainProbe',
    'querySubdomainGetData',
  ].includes(endpoint)
}

// Utility function to check if endpoint is in dynamic allowedEndpoints list
const isEndpointAllowed = (endpoint: string, allowedEndpoints: string[] = []): boolean => {
  return allowedEndpoints.includes(endpoint)
}

export const baseApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_URL}`,
    prepareHeaders: (headers, { getState, endpoint }) => {
      const state = getState() as RootState
      const token = state.session?.sessionToken

      headers.set('content-type', 'application/json')

      // API blocking logic - check if blocking is active and endpoint is not allowed
      if (state.session.isApiBlocked) {
        const isAllowed = isEndpointAllowed(endpoint, state.session.allowedEndpoints)
        if (!isAllowed && !isPublicEndpoint(endpoint)) {
          throw new Error(
            JSON.stringify({
              code: 'API_BLOCKED_2FA_REQUIRED',
              message: 'Please complete 2FA setup to continue',
              blockedEndpoint: endpoint,
              timestamp: Date.now(),
            }),
          )
        }
      }

      if (token && !isPublicEndpoint(endpoint)) {
        headers.set('authorization', token)
      }

      if ((token && endpoint === 'getDomainTest') || endpoint === 'getPublicDomainTest') {
        headers.set('accept', 'application/dns+json')
      }
      return headers
    },
  }),
  tagTypes: [
    'Access',
    'Billing',
    'Tracking',
    'Notifications',
    'Network',
    'FreeDns',
    'FeaturedProfiles',
    'DefaultRule',
    'Payment',
    'Product',
    'Status',
    'TwoFa',
    'Token',
    'Proxies',
    'Organization',
    'Member',
    'SubOrg',
    'Provision',
    'Devices',
    'Schedules',
    'User',
    'Filters',
    'Services',
    'Analytics',
    'ActivityLog',
    'Profiles',
    'ProfileOptions',
    'CustomRules',
    'AllCustomRules',
    'Group',
    'Services',
    'Reports',
  ],
  endpoints: () => ({}),
})

export const transformErrorResponse = (
  baseQueryReturnValue: FetchBaseQueryError,
): { status: number } & ControlDError => {
  const errorData = baseQueryReturnValue.data as {
    error: ControlDError
  }

  return {
    status: baseQueryReturnValue.status as number,
    code: errorData?.error?.code || '',
    message: errorData?.error?.message || '',
    actions: errorData?.error?.actions || undefined,
  }
}

export const isExceptionalErrorCode = (errorCode?: string): boolean =>
  [errorCodes.ERROR_MISSING_REQUIRED, errorCodes.ERROR_PAY_ME].includes(errorCode ?? '')

export const throwErrorFromResponseData = (response: {
  body: unknown
  error?: SerializedError & ControlDError
  success: boolean
  message?: string
}): void => {
  if (response?.error || !response?.success) {
    const error = {
      ...response.error,
      code: response.error?.code?.toString() || '',
    }

    if (response.error?.actions) {
      throw JSON.stringify({
        ...error,
        actions: response.error?.actions,
      })
    } else {
      throw error
    }
  }

  const body = response?.body as { error?: SerializedError & ControlDError }

  if (isExceptionalErrorCode(body?.error?.code?.toString())) {
    return
  }

  if (body?.error) {
    if (body?.error?.code) {
      const error = {
        ...body?.error,
        code: body?.error?.code?.toString() || '',
      }

      if (body?.error?.actions) {
        throw JSON.stringify({
          ...error,
          actions: body?.error?.actions,
        })
      } else {
        throw error
      }
    }

    throw { message: body?.error?.message }
  }
}
